/**
 * Encryption Service
 * Handles message encryption/decryption using switch-core's high-level APIs.
 * Routes all encryption through Rust core for consistent payload format across platforms.
 *
 * IMPORTANT: Content preparation logic is now in core (prepare_dm_message, prepare_group_message).
 * This ensures iOS, Android, and Web all use the same encryption format:
 * - TEXT: raw text content (no JSON envelope)
 * - GIPHY: URL string only
 * - FILE/ATTACHMENT: attachment JSON object
 */

import * as coreModuleApi from "@/core_bridge/module_api";
import { useStore } from "@/state/legacy_module_api";
import { extractContactPublicKeys, resolveContactPublicKey } from "@/data/adapters/contact_keys";

const corePrepareDM = coreModuleApi.prepareDMMessage;
const corePrepareGroup = coreModuleApi.prepareGroupMessage;
const coreBuildMessageContent = coreModuleApi.buildMessageContent;
const parseJwtPayload = coreModuleApi.parseJwtPayload;

export interface EncryptedMessage {
  encryptedContent: string;
  nonce: string;
  [key: string]: any;
}

export interface DeviceEncryptedMessage extends EncryptedMessage {
  senderKeyId?: string | null;
  targetDeviceId?: string | null;
  encVersion?: string | null;
  mlKemCiphertext?: string | null;
}

/**
 * Get receiver's public key from contactsById
 */
function getReceiverPublicKey(receiverId: string, contactsById: Record<string, any>): string | undefined {
  const receiver = contactsById?.[receiverId];
  return resolveContactPublicKey(receiver);
}

function getCurrentDeviceId(store: any): string | undefined {
  return (
    store.deviceId ||
    store.session?.deviceId ||
    (store.apiToken ? parseJwtPayload(store.apiToken)?.device_id : undefined)
  );
}

/**
 * Get content to encrypt based on message type
 * - FILE/ATTACHMENT: JSON string of attachment object
 * - GIPHY: URL string
 * - TEXT: raw text content
 *
 * The raw content is then wrapped in the versioned message-content envelope via
 * core `buildMessageContent`, which carries the HiLo environment INSIDE the
 * ciphertext (so the server never sees it) and tags the content kind. This
 * matches iOS/Android; the envelope is the single format encrypted on all
 * platforms. Every encrypt* path funnels through here, so wrapping once here
 * covers DM, self-chat, and group fanout.
 */
function getContentToEncrypt(message: any): { content: string; contentType: string } {
  let content: string;
  let contentType: string;

  if (message.attachment && typeof message.attachment === "object") {
    content = JSON.stringify(message.attachment);
    contentType = "FILE";
  } else if (message.contentType === "GIPHY") {
    content = message.content || "";
    contentType = "GIPHY";
  } else {
    content = message.content || "";
    contentType = message.contentType || "TEXT";
  }

  // @platform-only: envelope construction lives in core
  // (build_message_content_for_send); this is the platform send site calling
  // it, exactly as iOS/Android call uniffiBuildMessageContent. Core derives the
  // envelope kind from contentType and strips the environment for control
  // messages, so passing message.environment through is always safe. The
  // surrounding content-type routing is pre-existing web wire-format mapping.
  const wrapped = coreBuildMessageContent(content, contentType, message.environment ?? null);

  return { content: wrapped, contentType };
}

/**
 * Encrypt a DM message using switch-core's high-level API
 * Routes through Rust core for consistent payload format across Web/iOS/Android
 */
export function encryptDMMessage(message: any): any {
  const store = useStore.getState();

  try {
    // Get sender's secret key (base64)
    const mySecretKeyB64 =
      store.keyPair?.base64?.secretKey ||
      (store.keyPair as any)?.base64?.secret_key;
    const myPublicKeyB64 =
      store.keyPair?.base64?.publicKey ||
      (store.keyPair as any)?.base64?.public_key;
    const myUserId =
      (store as any).userId ||
      (store.apiToken ? parseJwtPayload(store.apiToken)?.user_id : undefined);
    if (!mySecretKeyB64 || !myUserId) {
      console.error("[encryption] encryptDMMessage: Missing sender credentials");
      return message;
    }

    // Get receiver info
    const receiverId = message.receiverId;
    if (!receiverId) {
      console.error("[encryption] encryptDMMessage: Missing receiverId");
      return message;
    }

    const receiverPublicKeyB64 =
      receiverId === myUserId
        ? myPublicKeyB64
        : getReceiverPublicKey(receiverId, store.contactsById || {});
    if (!receiverPublicKeyB64) {
      console.error("[encryption] encryptDMMessage: Missing receiver public key", { receiverId });
      return message;
    }

    // Get content based on message type
    const { content, contentType } = getContentToEncrypt(message);

    // Use core's high-level prepare function (handles encryption + ID generation)
    const prepared = corePrepareDM(
      content,
      receiverId,
      receiverPublicKeyB64,
      myUserId,
      mySecretKeyB64,
      contentType
    );

    // Return encrypted message (use existing ID if provided, otherwise use generated)
    const encryptedMessage = {
      ...message,
      id: message.id || prepared.id,
      nonce: prepared.nonce,
      encryptedContent: prepared.encrypted_content,
      contentType: prepared.content_type,
      threadKey: message.threadKey || prepared.thread_key,
    };

    // Remove plaintext content
    delete encryptedMessage.content;
    delete encryptedMessage.attachment;

    console.log("[encryption] encryptDMMessage: Message encrypted via core");
    return encryptedMessage;
  } catch (err) {
    console.error("[encryption] encryptDMMessage failed:", err);
    return message;
  }
}

/**
 * Encrypt a DM message with an explicit receiver public key.
 * Used for group fanout when contactsById may be stale.
 */
export function encryptDMMessageWithPublicKey(message: any, receiverPublicKeyB64: string): any {
  const store = useStore.getState();

  try {
    // Get sender's secret key (base64)
    const mySecretKeyB64 =
      store.keyPair?.base64?.secretKey ||
      (store.keyPair as any)?.base64?.secret_key;
    const myPublicKeyB64 =
      store.keyPair?.base64?.publicKey ||
      (store.keyPair as any)?.base64?.public_key;
    const myUserId =
      (store as any).userId ||
      (store.apiToken ? parseJwtPayload(store.apiToken)?.user_id : undefined);
    if (!mySecretKeyB64 || !myUserId) {
      console.error("[encryption] encryptDMMessageWithPublicKey: Missing sender credentials");
      return message;
    }

    const receiverId = message.receiverId;
    if (!receiverId) {
      console.error("[encryption] encryptDMMessageWithPublicKey: Missing receiverId");
      return message;
    }

    const sanitizedReceiverKey = String(receiverPublicKeyB64 || "")
      .replace(/\s+/g, "");
    const receiverPublicKey =
      receiverId === myUserId ? (myPublicKeyB64 || sanitizedReceiverKey) : sanitizedReceiverKey;

    if (!receiverPublicKey) {
      console.error("[encryption] encryptDMMessageWithPublicKey: Missing receiver public key", {
        receiverId,
      });
      return message;
    }

    const { content, contentType } = getContentToEncrypt(message);
    const prepared = corePrepareDM(
      content,
      receiverId,
      receiverPublicKey,
      myUserId,
      mySecretKeyB64,
      contentType
    );

    const encryptedMessage = {
      ...message,
      id: message.id || prepared.id,
      nonce: prepared.nonce,
      encryptedContent: prepared.encrypted_content,
      contentType: prepared.content_type,
      threadKey: message.threadKey || prepared.thread_key,
    };

    delete encryptedMessage.content;
    delete encryptedMessage.attachment;

    console.log("[encryption] encryptDMMessageWithPublicKey: Message encrypted via core");
    return encryptedMessage;
  } catch (err) {
    console.error("[encryption] encryptDMMessageWithPublicKey failed:", err);
    return message;
  }
}

/**
 * Encrypt a per-device DM envelope using the recipient key entry.
 */
export function encryptDMMessageForDevice(
  message: any,
  receiverKeyEntry: { publicKey: string; deviceId?: string | null; mlKemPublicKey?: string | null }
): DeviceEncryptedMessage | null {
  const store = useStore.getState() as any;

  try {
    const mySecretKeyB64 =
      store.keyPair?.base64?.secretKey ||
      (store.keyPair as any)?.base64?.secret_key;
    const myMlKemSecretKeyB64 =
      store.mlKemKeyPair?.base64?.secretKey ||
      (store.mlKemKeyPair as any)?.base64?.secret_key ||
      store.appState?.mlKemSecretKey ||
      store.appState?.ml_kem_secret_key;
    const myUserId =
      (store as any).userId ||
      (store.apiToken ? parseJwtPayload(store.apiToken)?.user_id : undefined);
    const myDeviceId = getCurrentDeviceId(store);

    if (!mySecretKeyB64 || !myUserId) {
      console.error("[encryption] encryptDMMessageForDevice: Missing sender credentials");
      return null;
    }

    const receiverId = message.receiverId;
    if (!receiverId) {
      console.error("[encryption] encryptDMMessageForDevice: Missing receiverId");
      return null;
    }

    const receiverPublicKey = String(receiverKeyEntry.publicKey || "").replace(/\s+/g, "");
    if (!receiverPublicKey) {
      console.error("[encryption] encryptDMMessageForDevice: Missing receiver public key", {
        receiverId,
      });
      return null;
    }

    const { content, contentType } = getContentToEncrypt(message);
    const prepareForDevice =
      (coreModuleApi as any).prepareDMMessageForDevice || corePrepareDM;
    const prepared = prepareForDevice(
      content,
      receiverId,
      receiverKeyEntry.deviceId || null,
      receiverPublicKey,
      receiverKeyEntry.mlKemPublicKey || null,
      myUserId,
      myDeviceId || null,
      mySecretKeyB64,
      myMlKemSecretKeyB64 || null,
      contentType
    );

    const encryptedMessage: DeviceEncryptedMessage = {
      ...message,
      id: message.id || prepared.id,
      nonce: prepared.nonce,
      encryptedContent: prepared.encrypted_content,
      contentType: prepared.content_type,
      threadKey: message.threadKey || prepared.thread_key,
      senderKeyId: prepared.sender_key_id || null,
      targetDeviceId: prepared.target_device_id || null,
      encVersion: prepared.enc_version || null,
      mlKemCiphertext: prepared.ml_kem_ciphertext || null,
    };

    delete encryptedMessage.content;
    delete encryptedMessage.attachment;
    return encryptedMessage;
  } catch (err) {
    console.error("[encryption] encryptDMMessageForDevice failed:", err);
    return null;
  }
}

export function getRecipientDeviceKeys(receiverId: string, contactsById: Record<string, any>): Array<{
  publicKey: string;
  deviceId?: string | null;
  mlKemPublicKey?: string | null;
}> {
  const receiver = contactsById?.[receiverId];
  return extractContactPublicKeys(receiver).filter((entry) => !!entry.publicKey);
}

/**
 * Encrypt a group message using switch-core's high-level API
 * Routes through Rust core for consistent payload format across Web/iOS/Android
 *
 * Uses the current key version from groupKeys map for encryption.
 * The keyVersion is included in the message for decryption.
 */
export function encryptGroupMessage(message: any): any {
  const store = useStore.getState();

  try {
    const groupId = message.groupId;
    if (!groupId) {
      console.error("[encryption] encryptGroupMessage: Missing groupId");
      return message;
    }

    // Groups are stored in threadMap
    const group = store.threadMap?.[groupId];

    // Try new groupKeys format first, fall back to legacy groupKey
    let groupKeyB64: string | undefined;
    let keyVersion: number = 1;

    if (group?.groupKeys && group?.currentKeyVersion) {
      // New format: use current key version
      keyVersion = group.currentKeyVersion;
      groupKeyB64 = group.groupKeys[keyVersion];
    } else if ((group as any)?.groupKey) {
      // Legacy format: single key, version 1
      groupKeyB64 = (group as any).groupKey;
      keyVersion = 1;
    }

    if (!groupKeyB64) {
      console.error("[encryption] encryptGroupMessage: Missing groupKey for group", groupId);
      return message;
    }

    // Get content based on message type
    const { content, contentType } = getContentToEncrypt(message);

    // Use core's high-level prepare function (handles encryption + ID generation)
    // Now includes key_version parameter
    const prepared = corePrepareGroup(content, groupId, groupKeyB64, contentType, keyVersion);

    // Return encrypted message (use existing ID if provided, otherwise use generated)
    const encryptedMessage = {
      ...message,
      id: message.id || prepared.id,
      nonce: prepared.nonce,
      encryptedContent: prepared.encrypted_content,
      contentType: prepared.content_type,
      keyVersion: prepared.key_version,
    };

    // Remove plaintext content
    delete encryptedMessage.content;
    delete encryptedMessage.attachment;

    console.log("[encryption] encryptGroupMessage: Message encrypted via core (keyVersion:", keyVersion, ")");
    return encryptedMessage;
  } catch (err) {
    console.error("[encryption] encryptGroupMessage failed:", err);
    return message;
  }
}

/**
 * Encrypt a message based on its type (DM or Group)
 */
export function encryptMessage(message: any): any {
  // Group messages use symmetric encryption
  if (message.groupId && !message.receiverId) {
    return encryptGroupMessage(message);
  }

  // DM messages use asymmetric encryption
  if (message.receiverId) {
    return encryptDMMessage(message);
  }

  // Self-chat (note to self) - encrypt as DM to self
  if (message.senderId === message.receiverId) {
    return encryptDMMessage(message);
  }

  console.warn("[encryption] encryptMessage: Unable to determine message type", message);
  return message;
}

export default {
  encryptMessage,
  encryptDMMessage,
  encryptDMMessageWithPublicKey,
  encryptGroupMessage,
};
